import {
  peutConsulterInstallation,
  peutPlanifierInstallation,
} from '../../Services/droits-installations';
import {
  afterRenderEffect,
  Component,
  computed,
  effect,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { Authentification } from '../../Services/authentification';
import { Installation } from '../../Services/installation';
import { EtatService } from '../../Services/etat';
import { TicketService } from '../../Services/ticket';
import { TicketBrut } from '../../Services/EntiteProjet';
import { CONFIG_ETATS_TICKET, TicketState } from '../../Services/ticket-state';
import {
  ContexteIntervention,
  DroitsInterventions,
  InterventionEditable,
  NatureIntervention,
  OptionsIntervention,
  SaisieIntervention,
  TicketInterventionOption,
  peutModifierBon,
} from '../../Services/intervention-edition';
import {
  InterventionApi,
  InterventionApiResultat,
  ErreurIntervention,
  PhotoIntervention,
  preparerEditionIntervention,
} from '../../Services/intervention-api';
import { InterventionActualisation } from '../../Services/intervention-actualisation';
import { InterventionFormulaire } from '../intervention-formulaire/intervention-formulaire';

const TAILLE_PAGE_TICKETS = 100;

/** Contexte transmis par le point d’entrée ; une édition sera relue depuis l’API. */
export type InterventionDialogData = {
  nature: NatureIntervention;
  ticketId: string | number | null;
  installationId: number | null;
  edition: InterventionEditable | null;
  /** Date civile et catégorie choisies dans le calendrier, uniquement pour une création. */
  preselection?: Pick<ContexteIntervention, 'date' | 'categorie'>;
  /** Complète une curative existante avec les champs du bon, sans POST ni nouveau lien. */
  transformation?: boolean;
};

/** Charge le contexte autorisé et relie le formulaire à la sauvegarde et à la modale. */
@Component({
  selector: 'app-intervention-dialog',
  imports: [InterventionFormulaire],
  templateUrl: './intervention-dialog.html',
  styleUrl: './intervention-dialog.css',
})
export class InterventionDialog {
  readonly data = inject<InterventionDialogData>(MAT_DIALOG_DATA);
  readonly auth = inject(Authentification);
  private readonly api = inject(InterventionApi);
  private readonly actualisation = inject(InterventionActualisation);
  private readonly installations = inject(Installation);
  private readonly etat = inject(EtatService);
  private readonly tickets = inject(TicketService);
  private readonly dialog = inject(MatDialogRef<InterventionDialog>);
  private readonly notification = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly composantFormulaire = viewChild(InterventionFormulaire);
  private readonly formulaireElement = viewChild<ElementRef<HTMLElement>>('formulaire');

  private enregistrementEnCours = false;
  private resultat: InterventionApiResultat | null = null;
  private parcCreationId: number | null = null;
  // La session et le numéro de requête empêchent une ancienne réponse de modifier la modale.
  private readonly session = this.auth.sessionVersion;
  private requeteTickets = 0;
  // Conserve le ticket du contexte, même s’il n’apparaît pas dans les pages de la liste.
  private ticketInitial: TicketInterventionOption | null = null;

  readonly photosExistantes = signal<PhotoIntervention[]>([]);
  readonly autorisationServeur = signal(true);
  readonly contexte = signal<ContexteIntervention>({});
  readonly edition = signal(this.data.edition);
  readonly options = signal<OptionsIntervention>({
    installations: [],
    tickets: [],
  });
  readonly chargementInitial = signal(true);
  readonly erreurInitiale = signal('');
  readonly chargementTickets = signal(false);
  readonly erreurTickets = signal('');
  readonly installationSelectionnee = signal('');

  private readonly planificationRequise =
    this.data.nature === 'planification' || !!this.data.transformation;

  /** Filtre le parc courant avec les rôles du JWT courant, y compris après un refresh. */
  readonly optionsAutorisees = computed<OptionsIntervention>(() => {
    const options = this.options();
    return {
      ...options,
      installations: options.installations.filter((i) => this.peutChoisirInstallation(i.id)),
    };
  });

  /** En édition, les droits sont nécessaires sur la centrale d’origine et celle de destination. */
  readonly droits = computed<DroitsInterventions>(() => {
    const utilisateur = this.auth.tokenInfoSignal();
    const origine = this.edition()?.valeurs.installationId;
    const selection = this.installationSelectionnee();
    const planifier = selection
      ? peutPlanifierInstallation(utilisateur, Number(selection))
      : this.optionsAutorisees().installations.some((i) =>
          peutPlanifierInstallation(utilisateur, i.id),
        );
    return {
      planifier,
      modifierPlanification:
        planifier && (!origine || peutPlanifierInstallation(utilisateur, Number(origine))),
    };
  });

  /**
   * Verrouille la fermeture pendant l’envoi et notifie les vues après confirmation de l’API.
   * Les erreurs remontent au formulaire, qui conserve le brouillon et affiche leur détail.
   */
  readonly enregistrer = async (saisie: SaisieIntervention): Promise<void> => {
    if (!this.actif() || this.enregistrementEnCours) throw new Error('Formulaire indisponible');
    const utilisateur = this.auth.tokenInfoSignal();
    const origine = this.edition();
    // Revérifie au moment d’écrire : les permissions d’ouverture ne sont pas une autorisation durable.
    if (
      !utilisateur ||
      !this.autorisationServeur() ||
      !this.peutChoisirInstallation(saisie.installationId) ||
      !this.optionsAutorisees().installations.some((i) => i.id === saisie.installationId) ||
      (origine && !this.peutChoisirInstallation(Number(origine.valeurs.installationId))) ||
      (!this.data.transformation &&
        origine?.nature === 'bon' &&
        !peutModifierBon(utilisateur, origine.auteurId, Number(origine.valeurs.installationId)))
    ) {
      throw new ErreurIntervention(
        'Vous n’avez pas le droit de modifier cette intervention sur cette centrale.',
      );
    }
    this.enregistrementEnCours = true;
    this.dialog.disableClose = true;
    try {
      const edition = this.edition();
      if (
        this.data.transformation &&
        (!edition ||
          saisie.id !== edition.id ||
          saisie.nature !== 'bon' ||
          saisie.installationId !== Number(edition.valeurs.installationId) ||
          saisie.ticketId !== Number(edition.valeurs.ticketId))
      )
        throw new Error(
          'Le bon doit conserver l’intervention, la centrale et le ticket d’origine.',
        );
      // Une planification peut changer de catégorie : sa route utilise la catégorie d’origine.
      const resultat =
        edition?.nature === 'planification'
          ? await this.api.enregistrer(saisie, edition.valeurs.categorie)
          : await this.api.enregistrer(saisie);
      if (!this.actif()) return;
      this.resultat = resultat;
      if (saisie.id === null && saisie.nature === 'planification') {
        const categorie = saisie.categorie === 'preventive' ? 'préventive' : 'curative';
        this.notification.open(`Intervention ${categorie} planifiée avec succès.`, 'Fermer', {
          duration: 5000,
          politeness: 'polite',
        });
      }
      this.actualisation.notifier(resultat.installationId, resultat.ticketId);
      // Une modification peut déplacer le rattachement : actualiser aussi la vue d’origine.
      if (edition) {
        const installationOrigine = Number(edition.valeurs.installationId);
        const ticketOrigine = Number(edition.valeurs.ticketId) || null;
        if (installationOrigine !== resultat.installationId || ticketOrigine !== resultat.ticketId)
          this.actualisation.notifier(installationOrigine, ticketOrigine);
      }
    } finally {
      this.enregistrementEnCours = false;
      this.dialog.disableClose = this.composantFormulaire()?.modifie() ?? false;
    }
  };

  /** Installe les gardes de contexte, la protection du brouillon et le focus initial. */
  constructor() {
    // Une création appartient au parc sélectionné à l’ouverture.
    effect(() => {
      const parcId = this.etat.parcSelectionne()?.id ?? null;
      if (!this.data.edition && this.parcCreationId !== null && parcId !== this.parcCreationId) {
        this.dialog.close();
      }
    });
    // Bloque Échap et le clic extérieur si la saisie risque d’être perdue.
    effect(() => {
      const formulaire = this.composantFormulaire();
      this.dialog.disableClose = !!formulaire && (formulaire.envoi() || formulaire.modifie());
    });
    // Le formulaire arrive après les données : attendre son rendu pour cibler son titre.
    afterRenderEffect(() => {
      this.formulaireElement()?.nativeElement.querySelector<HTMLElement>('h2')?.focus();
    });
    // Le contexte ne doit pas survivre à un changement d’utilisateur ou de page.
    this.auth.sessionChanges.pipe(takeUntilDestroyed()).subscribe(() => this.dialog.close());
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.dialog.close());
    void this.charger();
  }

  /** Ferme sur le résultat serveur une fois que le formulaire a traité le succès. */
  terminer(): void {
    if (this.actif() && this.resultat) this.dialog.close(this.resultat);
  }

  /** Autorise l’annulation explicite du brouillon, sauf pendant une sauvegarde en cours. */
  fermer(): void {
    if (this.enregistrementEnCours) return;
    this.dialog.close();
  }

  /** Initialise ou recharge les choix, le contexte et les droits d’édition depuis le serveur. */
  async charger(): Promise<void> {
    this.chargementInitial.set(true);
    this.erreurInitiale.set('');
    try {
      const editionInitiale = this.data.edition;
      if (!editionInitiale) {
        this.parcCreationId = this.etat.parcSelectionne()?.id ?? null;
        if (this.parcCreationId === null) {
          this.erreurInitiale.set('Sélectionnez un parc avant de créer une intervention.');
          return;
        }
      }
      // Relire évite de modifier avec des valeurs ou des permissions devenues obsolètes.
      const enregistree = editionInitiale ? await this.relireEdition(editionInitiale) : null;
      if (!this.actif()) return;
      const ticketId = enregistree ? enregistree.ticketId : this.data.ticketId;
      this.autorisationServeur.set(enregistree?.permissions.modifier ?? true);
      this.photosExistantes.set(enregistree?.nature === 'bon' ? enregistree.photos : []);
      // La création réutilise la liste du parc courant, y compris une requête déjà en cours.
      // L’édition conserve l’accès au contexte historique de l’intervention.
      const [installations, ticket] = await Promise.all([
        editionInitiale
          ? this.installations.listeInstallations()
          : this.etat.obtenirInstallationsParcCourant(),
        ticketId ? this.tickets.getTicket(String(ticketId)) : Promise.resolve(null),
      ]);
      if (!this.actif()) return;
      if (!installations || (ticketId && !ticket)) throw new Error('Contexte indisponible');
      const installationDemandee = Number(
        enregistree?.installationId ??
          ticket?.installation?.id ??
          ticket?.id_installation ??
          this.data.installationId,
      );
      // Un préremplissage libre hors parc est effacé ; un ticket imposé hors parc est refusé.
      const installationId =
        !editionInitiale && !ticketId && !installations.some((i) => i.id === installationDemandee)
          ? 0
          : installationDemandee;
      if (installationId > 0 && !this.peutChoisirInstallation(installationId)) {
        throw new ErreurIntervention(
          'Vous n’avez pas le droit de créer ou modifier cette intervention sur cette centrale.',
        );
      }
      this.ticketInitial = ticket ? this.optionTicket(ticket, installationId) : null;
      if (
        ticketId &&
        (!this.ticketInitial || !installations.some((i) => i.id === installationId))
      ) {
        throw new Error('Centrale du ticket indisponible');
      }
      this.options.set({
        installations: installations
          .map((i) => ({ id: i.id, nom: i.nom }))
          .sort((a, b) => a.nom.localeCompare(b.nom, 'fr')),
        tickets: this.ticketInitial ? [this.ticketInitial] : [],
      });
      this.contexte.set({
        ...(!editionInitiale && this.data.nature === 'planification' ? this.data.preselection : {}),
        ...(installationId > 0 ? { installationId } : {}),
        ...(this.ticketInitial ? { ticketId: this.ticketInitial.id } : {}),
      });
      if (enregistree) this.edition.set(preparerEditionIntervention(enregistree));
      if (installationId > 0) {
        this.installationSelectionnee.set(String(installationId));
        // En création depuis un ticket, le contexte est verrouillé et le ticket
        // a déjà été chargé. Sa liste complète n’est pas nécessaire.
        if (this.data.edition || !this.ticketInitial) {
          await this.chargerTickets(String(installationId));
        }
      }
    } catch (erreur) {
      if (this.actif())
        this.erreurInitiale.set(
          erreur instanceof ErreurIntervention
            ? erreur.message
            : this.data.transformation
              ? 'Impossible de charger le bon correspondant à cette planification. Réessayez.'
              : 'Impossible de charger les centrales ou le ticket. Réessayez.',
        );
    } finally {
      if (this.actif()) this.chargementInitial.set(false);
    }
  }

  /**
   * Charge toutes les pages et tous les états de traitement de la centrale sélectionnée.
   * Seule la dernière demande peut publier ses résultats ou son erreur.
   */
  async chargerTickets(installation: string): Promise<void> {
    // Invalide aussi la requête précédente lorsque la sélection est simplement effacée.
    const requete = ++this.requeteTickets;
    const requeteActive = () => this.actif() && requete === this.requeteTickets;
    this.installationSelectionnee.set(installation);
    this.erreurTickets.set('');
    const installationId = Number(installation);
    if (installation && !this.peutChoisirInstallation(installationId)) {
      this.options.update((o) => ({ ...o, tickets: [] }));
      this.chargementTickets.set(false);
      this.erreurTickets.set('Vous n’avez pas accès à cette centrale pour cette intervention.');
      return;
    }
    const tickets = new Map<number, TicketInterventionOption>();
    if (this.ticketInitial?.installationId === installationId) {
      tickets.set(this.ticketInitial.id, this.ticketInitial);
    }
    this.options.update((o) => ({ ...o, tickets: [...tickets.values()] }));
    // Une planification préventive ne nécessite pas de liste de tickets.
    const categorie =
      this.edition()?.valeurs.categorie ??
      (this.data.ticketId ? 'curative' : (this.data.preselection?.categorie ?? 'preventive'));
    const ticketsRequis =
      this.composantFormulaire()?.ticketRequis() ??
      (this.data.nature === 'bon' || categorie === 'curative');
    if (!installation || !ticketsRequis) {
      this.chargementTickets.set(false);
      return;
    }
    this.chargementTickets.set(true);
    try {
      let page = 1;
      let nombreRecus = 0;
      let suite: boolean;
      do {
        // Aucun filtre d’état : les tickets résolus restent sélectionnables.
        const reponse = await this.installations.getInstallationTickets(
          installationId,
          undefined,
          page,
          TAILLE_PAGE_TICKETS,
        );
        if (!requeteActive()) return;
        if (!reponse) throw new Error('Tickets indisponibles');
        for (const ticket of reponse.tickets) {
          const option = this.optionTicket(ticket, installationId);
          if (option) tickets.set(option.id, option);
        }
        nombreRecus += reponse.tickets.length;
        // Préférer les métadonnées ; à défaut, une page pleine peut avoir une suite.
        suite =
          reponse.pagination?.pages != null
            ? page < reponse.pagination.pages
            : reponse.pagination?.total != null
              ? nombreRecus < reponse.pagination.total
              : reponse.tickets.length === TAILLE_PAGE_TICKETS;
        if (suite && !reponse.tickets.length) throw new Error('Liste de tickets incomplète');
        ++page;
      } while (suite);
      // Publier seulement la liste complète : une erreur sur une page reste réessayable.
      this.options.update((o) => ({ ...o, tickets: [...tickets.values()] }));
    } catch {
      if (requeteActive())
        this.erreurTickets.set('Impossible de charger les tickets de cette centrale. Réessayez.');
    } finally {
      if (requeteActive()) this.chargementTickets.set(false);
    }
  }

  /** Vérifie après chaque attente que la réponse concerne toujours cette modale et ce parc. */
  private actif(): boolean {
    return (
      !this.destroyRef.destroyed &&
      this.session === this.auth.sessionVersion &&
      (this.data.edition !== null ||
        this.parcCreationId === null ||
        this.etat.parcSelectionne()?.id === this.parcCreationId)
    );
  }

  /** Les bons suivent le périmètre de consultation ; les planifications exigent un rôle de gestion. */
  private peutChoisirInstallation(id: number): boolean {
    const utilisateur = this.auth.tokenInfoSignal();
    return this.planificationRequise
      ? peutPlanifierInstallation(utilisateur, id)
      : peutConsulterInstallation(utilisateur, id);
  }

  /** Identifie la route de relecture avec la catégorie d’origine d’une planification. */
  private relireEdition(edition: InterventionEditable): Promise<InterventionApiResultat> {
    if (this.data.transformation) return this.relireTransformation(edition);
    if (edition.nature === 'planification') {
      return this.api.lire('planification', edition.id, edition.valeurs.categorie);
    }
    return this.api.lire('bon', edition.id);
  }

  /** Les routes curative et bon désignent la même intervention ; vérifier aussi son rattachement. */
  private async relireTransformation(
    edition: InterventionEditable,
  ): Promise<InterventionApiResultat> {
    if (
      this.data.nature !== 'bon' ||
      edition.nature !== 'planification' ||
      edition.valeurs.categorie !== 'curative'
    )
      throw new ErreurIntervention('Seule une planification curative peut être complétée en bon.');
    const planification = await this.api.lire('planification', edition.id, 'curative');
    if (!this.actif()) throw new Error('Contexte indisponible');
    if (
      planification.nature !== 'planification' ||
      !planification.ticketId ||
      !planification.permissions.modifier ||
      !peutPlanifierInstallation(this.auth.tokenInfoSignal(), planification.installationId)
    )
      throw new ErreurIntervention(
        'Cette planification ne peut pas être transformée : elle doit être modifiable et rattachée à un ticket.',
      );
    const bon = await this.api.lire('bon', edition.id);
    if (
      bon.nature !== 'bon' ||
      bon.installationId !== planification.installationId ||
      bon.ticketId !== planification.ticketId
    )
      throw new ErreurIntervention(
        'Le bon ne correspond pas à la centrale ou au ticket de cette planification.',
      );
    return bon;
  }

  /**
   * Écarte les identifiants invalides et les tickets explicitement rattachés à une autre centrale.
   * Normalise les états textuels ou numériques ; un ticket fermé appartient au groupe Résolus.
   */
  private optionTicket(
    ticket: TicketBrut,
    installationId: number,
  ): TicketInterventionOption | null {
    const id = Number(ticket.id);
    if (
      !Number.isSafeInteger(id) ||
      id <= 0 ||
      !Number.isSafeInteger(installationId) ||
      installationId <= 0 ||
      Number(ticket.installation?.id ?? ticket.id_installation ?? installationId) !== installationId
    )
      return null;
    const etatBrut = String(ticket.etat ?? '')
      .trim()
      .toLowerCase();
    const etat = ticket.date_fermeture
      ? TicketState.resolu
      : (Object.values(TicketState).find(
          (etat) => etat === etatBrut || CONFIG_ETATS_TICKET[etat].etat === etatBrut,
        ) ?? null);
    return {
      id,
      installationId,
      etat,
      numero: String(ticket.numero ?? ticket.reference ?? ticket.ref ?? id),
      libelle: ticket.libelle ?? ticket.objet ?? '',
    };
  }
}
